import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { CustomerLocale, CustomerProfile, Diet } from '@capsico/contracts';
import { fetchProfile, saveProfile } from '../api/customer';
import { errorMessage } from '../api/client';
import { setLocale } from '../i18n';
import { useFormDraft } from './useFormDraft';
import { showToast } from './useToast';

export interface ProfileForm {
  name: string;
  email: string;
  birthday: string;
  diet: Diet | null;
  locale: CustomerLocale;
}

const toForm = (profile: CustomerProfile): ProfileForm => ({
  name: profile.name ?? '',
  email: profile.email ?? '',
  birthday: profile.birthday ?? '',
  diet: profile.diet,
  locale: profile.locale,
});

/** The signed-in customer's profile (read with SWR). */
export function useProfileQuery(enabled: boolean) {
  return useQuery({
    queryKey: ['profile'],
    queryFn: fetchProfile,
    enabled,
    staleTime: 5 * 60_000,
  });
}

/**
 * Profile form: name, diet, email, birthday, app language. Drafted on this device from the start
 * (no secrets in it). Saving updates the cache at once and rolls back if the server refuses.
 */
export function useProfileForm(options: {
  messages: { saved: string; nameRequired: string; dietRequired: string };
  onSaved?: () => void;
}) {
  const client = useQueryClient();
  const query = useProfileQuery(true);
  const base = useMemo(() => (query.data ? toForm(query.data) : null), [query.data]);
  const [form, setForm] = useState<ProfileForm | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (base && form === null) setForm(base);
  }, [base, form]);

  const draft = useFormDraft<ProfileForm>('customer-profile:me', form ?? (base as ProfileForm), {
    enabled: Boolean(base && form),
    isEmpty: (value) => !value || (value.name.trim() === '' && value.diet === null),
    ...(base ? { base } : {}),
  });

  const save = useMutation({
    mutationFn: (value: ProfileForm) =>
      saveProfile({
        name: value.name.trim(),
        email: value.email.trim() === '' ? null : value.email.trim(),
        ...(value.birthday ? { birthday: value.birthday } : { birthday: null }),
        diet: value.diet as Diet,
        locale: value.locale,
        ...(query.data && query.data.version > 0 ? { expectedVersion: query.data.version } : {}),
      }),
    onMutate: async (value) => {
      await client.cancelQueries({ queryKey: ['profile'] });
      const previous = client.getQueryData<CustomerProfile>(['profile']);
      if (previous) {
        client.setQueryData<CustomerProfile>(['profile'], {
          ...previous,
          name: value.name.trim(),
          diet: value.diet,
          locale: value.locale,
          complete: true,
        });
      }
      return { previous };
    },
    onError: (failure, _value, context) => {
      if (context?.previous) client.setQueryData(['profile'], context.previous);
      setError(errorMessage(failure));
    },
    onSuccess: (profile) => {
      client.setQueryData(['profile'], profile);
      void client.invalidateQueries({ queryKey: ['me'] });
      draft.clear();
      setLocale(profile.locale);
      setForm(toForm(profile));
      showToast(options.messages.saved, { tone: 'positive' });
      options.onSaved?.();
    },
  });

  const patch = (change: Partial<ProfileForm>) => {
    setError(null);
    setForm((current) => (current ? { ...current, ...change } : current));
  };

  return {
    loading: query.isLoading,
    failed: query.isError,
    retry: () => void query.refetch(),
    form,
    patch,
    error,
    saving: save.isPending,
    draftRestored: draft.restored !== null,
    keepDraft: () => {
      const kept = draft.keep();
      if (kept) setForm(kept);
    },
    discardDraft: draft.discard,
    submit: () => {
      if (!form) return;
      if (form.name.trim().length < 2) return setError(options.messages.nameRequired);
      if (!form.diet) return setError(options.messages.dietRequired);
      save.mutate(form);
    },
  };
}
